import React, { useEffect } from 'react';
import * as yup from 'yup';
import FormControl from '../../../Form/FormControl';
import DatePicker from '../../../Base/DatePicker/DatePicker';
import TimePicker from '../../../Base/TimePicker/TimePicker';
import moment, { Moment } from 'moment';
import Checkbox from '../../../Base/Checkbox/Checkbox';
import SubmitButton from '../../../Button/SubmitButton';
import Select, { SelectValue } from '../../../Base/Select/Select';
import DoctorSelect from '../../../../Modules/Doctor/Components/DoctorSelect/DoctorSelect';
import FormWrapper from '../../../Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useTranslation from '../../../../Hooks/useTranslation';
import TextArea from '../../../Base/TextArea/TextArea';
import PatientTimeZoneInfo from '../../../Patient/PatientTimeZoneInfo';
import useTimezoneInfo from '../../../../Modules/Base/Hooks/useTimezoneInfo';
import CalendarTimezoneWarning from '../../CalendarTimezoneWarning/CalendarTimezoneWarning';

export interface CalendarHealthPlanEventFormValues {
	medicalProfessional: SelectValue;
	meetingType: SelectValue;
	date: Moment;
	timeFrom: Moment;
	timeTo: Moment;
	paidMeeting: boolean;
	note?: string;
}

interface Props {
	onSubmit: (values: CalendarHealthPlanEventFormValues) => void;
	defaultValues?: any;
	patientId: string;
}

const CalendarHealthPlanEventForm: React.FC<Props> = ({
	onSubmit,
	defaultValues,
	patientId,
}) => {
	const { t } = useTranslation();
	const {
		usersTimeDiff,
		patientTimezone,
		timeFrom,
		timeTo,
		handlePatientTimeDiffChange,
		handleGetPatientsTimes,
	} = useTimezoneInfo(defaultValues.timeFrom, defaultValues.timeTo);

	const validationSchema = yup.object({
		medicalProfessional: yup
			.object()
			.test(
				'empty-check',
				t('validation.calendarPatientEvent.medicalProfessional') ?? '',
				({ value }) => Boolean(value),
			)
			.required(t('validation.calendarPatientEvent.medicalProfessional')),
		meetingType: yup
			.mixed()
			.required(t('validation.calendarPatientEvent.meetingType')),
		date: yup.date().required(t('validation.calendarPatientEvent.date')),
		timeFrom: yup
			.date()
			.nullable()
			.default(undefined)
			.required(t('validation.calendarPatientEvent.timeFrom')),
		timeTo: yup
			.date()
			.nullable()
			.default(undefined)
			.required(t('validation.calendarPatientEvent.timeTo')),
		note: yup.string().nullable().notRequired(),
	});

	const formMethods = useForm({
		defaultValues: defaultValues || {},
		resolver: yupResolver(validationSchema),
	});

	useEffect(() => {
		formMethods.reset(defaultValues);
		handleGetPatientsTimes(defaultValues);
	}, [defaultValues]);

	const handleSubmit = (values: any) => {
		onSubmit(values as CalendarHealthPlanEventFormValues);
	};

	return (
		<FormWrapper formMethods={formMethods}>
			<FormControl
				name='medicalProfessional'
				label='base.professionalName'
				isFormContextDisabled
				isRequired>
				<DoctorSelect />
			</FormControl>
			<FormControl
				label='base.typeOfEvent'
				name='meetingType'
				isFormContextDisabled
				isRequired>
				<Select
					placeholder={t('base.shared.pick')}
					options={[
						{
							value: 'telemedicine',
							label: 'base.meetingType.telemedicine',
						},
						{
							value: 'onSite',
							label: 'base.meetingType.onSite',
						},
					]}
				/>
			</FormControl>
			{patientTimezone && usersTimeDiff && (
				<CalendarTimezoneWarning
					patientTimezone={patientTimezone}
					usersTimeDiff={usersTimeDiff}
				/>
			)}
			<div className={'row'}>
				<div className={'col-4'}>
					<FormControl
						label='base.shared.date'
						name='date'
						isFormContextDisabled
						isRequired>
						<DatePicker minDate={moment()} />
					</FormControl>
				</div>
				<div className={'col-4'}>
					<FormControl
						label='base.shared.from'
						name='timeFrom'
						isFormContextDisabled
						onChange={() => handleGetPatientsTimes(formMethods.getValues())}
						isRequired>
						<TimePicker />
					</FormControl>
					<PatientTimeZoneInfo
						patientId={patientId}
						time={timeFrom}
						onChange={handlePatientTimeDiffChange}
					/>
				</div>
				<div className={'col-4'}>
					<FormControl
						label='base.shared.to'
						name='timeTo'
						isFormContextDisabled
						onChange={() => handleGetPatientsTimes(formMethods.getValues())}
						isRequired>
						<TimePicker />
					</FormControl>
					<PatientTimeZoneInfo
						patientId={patientId}
						time={timeTo}
						onChange={handlePatientTimeDiffChange}
					/>
				</div>
			</div>
			<FormControl
				label='base.shared.noteVisibleForPatient'
				name='note'
				isFormContextDisabled>
				<TextArea />
			</FormControl>
			<FormControl name='paidMeeting' isFormContextDisabled hideRequired>
				<Checkbox tx='component.calendarPatientEvent.paidMeeting' />
			</FormControl>
			<div className={'d-flex flex-row align-items-center mt-4'}>
				<SubmitButton
					onSubmit={handleSubmit}
					title='component.eventDetail.saveToCalendarAndSendInfoToPatient'
				/>
			</div>
		</FormWrapper>
	);
};

export default CalendarHealthPlanEventForm;
